import { memo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import ListAltIcon from "@mui/icons-material/ListAlt";

import PageHero from "src-next/shared/ui/sections/PageHero";

import type { UseQuestionnaireBuilderResult } from "../hooks/useQuestionnaireBuilder";
import QuestionsActionsCard from "./QuestionsActionsCard";
import QuestionsSection from "./QuestionsSection";
import QuestionsListSection from "./QuestionsListSection";
import NextStepsDialog from "./NextStepsDialog";
import { LargeContentBox } from "@/components/common";
import { PageHeader } from "@/components/common/PageHeader";

export interface QuestionnaireBuilderViewProps extends UseQuestionnaireBuilderResult {}

const QuestionnaireBuilderView = memo(
  (props: QuestionnaireBuilderViewProps) => {
    const {
      activeQuestionIndex,
      activeQuestion,
      isConfigCollapsed,
      isNextStepsDialogOpen,
      isQuestionnaireEmpty,
      control,
      controller: Controller,
      handleSubmitQuestionnaire,
      isSubmitting,
      isDraftSubmitting,
      isAddingQuestion,
      isTitleEmpty,
      hasUnsavedChanges,
      questionFields,
      watchedValues,
      sensors,
      dndCollisionDetection,
      dndSortingStrategy,
      handleDragEnd,
      handleAddNewQuestion,
      handleDeleteQuestion,
      handleDuplicateQuestion,
      handleChangeActiveQuestion,
      handleToggleConfigCollapsed,
      handleSaveConfigSection,
      handleCloseNextStepsDialog,
      pageHeroTitle,
      intermediateSaveButtonLabel,
    } = props;

    return (
      <LargeContentBox>
        <Stack sx={{ width: "100%" }} spacing={3}>
          <PageHeader title={pageHeroTitle} icon={ListAltIcon} />

          <Stack spacing={2.5}>
            <Box sx={{ width: "100%", minWidth: 0 }}>
              <QuestionsActionsCard
                control={control}
                Controller={Controller}
                isConfigCollapsed={isConfigCollapsed}
                onToggleCollapsed={handleToggleConfigCollapsed}
              />
            </Box>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2.5}
              alignItems="stretch"
            >
              <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
                <QuestionsSection
                  activeQuestionIndex={activeQuestionIndex}
                  activeQuestion={activeQuestion}
                  isQuestionnaireEmpty={isQuestionnaireEmpty}
                  control={control}
                  Controller={Controller}
                  isConfigCollapsed={isConfigCollapsed}
                  onAddQuestion={handleAddNewQuestion}
                  isAddQuestionDisabled={isAddingQuestion}
                />
              </Stack>

              <Box sx={{ width: { xs: "100%", md: 550 }, flexShrink: 0 }}>
                <QuestionsListSection
                  questionFields={questionFields}
                  watchedValues={watchedValues}
                  activeQuestionIndex={activeQuestionIndex}
                  sensors={sensors}
                  dndCollisionDetection={dndCollisionDetection}
                  dndSortingStrategy={dndSortingStrategy}
                  onDragEnd={handleDragEnd}
                  onDeleteQuestion={handleDeleteQuestion}
                  onDuplicateQuestion={handleDuplicateQuestion}
                  onChangeActiveQuestion={handleChangeActiveQuestion}
                  onSaveConfig={handleSaveConfigSection}
                  intermediateSaveButtonLabel={intermediateSaveButtonLabel}
                  onSubmitQuestionnaire={handleSubmitQuestionnaire}
                  isDraftSubmitting={isDraftSubmitting}
                  isQuestionnaireEmpty={isQuestionnaireEmpty}
                  isSubmitting={isSubmitting}
                  isTitleEmpty={isTitleEmpty}
                  hasUnsavedChanges={hasUnsavedChanges}
                  isConfigCollapsed={isConfigCollapsed}
                />
              </Box>
            </Stack>
          </Stack>

          {isNextStepsDialogOpen && (
            <NextStepsDialog
              open={isNextStepsDialogOpen}
              onClose={handleCloseNextStepsDialog}
            />
          )}
        </Stack>
      </LargeContentBox>
    );
  },
);

QuestionnaireBuilderView.displayName = "QuestionnaireBuilderView";

export default QuestionnaireBuilderView;
